import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolDefinition, ToolExecutionContext } from '../types';

import { ConversationTool } from './ConversationTool';
import { EditAIVoiceoverTool } from './EditAIVoiceoverTool';
import { PAUSE_TAG_REGEX } from './pause-tags';
import { computeSceneGroupShortIds, resolveInsertSlotAfter } from './scene-group-addressing';
import { AI_VOICEOVER_DISABLED_MESSAGE } from './constants';
import { SearchVoicesTool } from './SearchVoicesTool';

import { LanguageEnum } from 'js/src/client';
import { getCanUseAIVoiceover } from 'js/src/data/account/account-selectors';
import { fetchDefaultAIVoice } from 'js/src/data/ai-voices-and-avatars';
import { generateVoiceoverAsset } from 'js/src/data/video-planner/video-planner-utils';
import type { RootState } from 'js/src/libs/RootState';
import {
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const addVoiceoverSceneGroupSchema = z.object({
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the new voiceover scene group should be inserted immediately after. Omit to insert it at the very start of the video.',
        ),
    transcript: z.string().describe('The narration text to convert to voiceover audio'),
    // Description is set in extendParameters: it names search_voices, whose module and this
    // one import each other, so reading SearchVoicesTool.toolName here (at module load) would
    // hit its temporal dead zone. extendParameters runs lazily, at getToolDefinition time.
    voice_slug: z.string().optional(),
    language_code: z
        .enum(
            Object.values(LanguageEnum).filter((lang) => lang !== LanguageEnum.XX) as [
                LanguageEnum,
                ...LanguageEnum[],
            ],
        )
        .optional()
        .describe('Language code for the voiceover. Defaults to "en"'),
});
export type AddVoiceoverSceneGroupArgs = z.infer<typeof addVoiceoverSceneGroupSchema>;

export class AddAIVoiceoverSceneGroupTool extends ConversationTool<
    typeof addVoiceoverSceneGroupSchema
> {
    public static readonly toolName = 'add_voiceover_scene_group';

    protected override readonly schema = addVoiceoverSceneGroupSchema;
    protected override readonly toolName = AddAIVoiceoverSceneGroupTool.toolName;
    protected override readonly toolDescription = `Add a new voiceover scene group to the video at the specified index. This generates AI voiceover audio from the provided transcript text and creates an overlay-free scene group with that voiceover and its synced transcript. If you do not have a script, write one yourself from the user's request and any source material — do not ask the user to write it. Pause tags (\`<pause seconds="N"/>\`) can't be included in the transcript here — they'd be read aloud. Generate the voiceover with the plain script, then add pauses with ${EditAIVoiceoverTool.toolName}.`;

    // voice_slug's description names search_voices, whose module cyclically imports this one.
    // Reading SearchVoicesTool.toolName at module load (in the schema) would dead-zone, so it's
    // filled in here — extendParameters runs at getToolDefinition, after all modules have loaded.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        (parameters.properties.voice_slug as { description?: string }).description =
            `The AI voice slug to use for generation. Use a slug returned by the ${SearchVoicesTool.toolName} tool. Required when a specific voice was requested.`;
        return parameters;
    }

    public override getToolDisplayName(): string {
        return 'Adding voiceover scene group';
    }

    public get statusIcon(): string {
        return 'record_voice_over';
    }

    public async execute(args: AddVoiceoverSceneGroupArgs, context: ToolExecutionContext) {
        if (!getCanUseAIVoiceover(this.getState())) {
            return `Error: ${AI_VOICEOVER_DISABLED_MESSAGE}`;
        }

        const {
            after_scene_group_id,
            transcript,
            voice_slug,
            language_code = LanguageEnum.EN,
        } = args;
        const { parentActionGroup } = context;

        // Resolve the insert slot up front (fail-closed on an unknown anchor id).
        const slot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        // Pauses must never reach TTS — they'd be spoken/garbled instead of becoming silence. A
        // fresh RegExp avoids the sticky-lastIndex pitfall of the shared global-flagged regex.
        if (new RegExp(PAUSE_TAG_REGEX.source).test(transcript)) {
            return `Error: Pause tags (\`<pause seconds="N"/>\`) can't be included when generating a voiceover — they'd be read aloud. Generate the voiceover with the plain script, then add pauses with ${EditAIVoiceoverTool.toolName}.`;
        }

        try {
            let resolvedVoiceSlug = voice_slug;
            if (!resolvedVoiceSlug) {
                const defaultVoice = await this.dispatch(fetchDefaultAIVoice());
                if (!defaultVoice?.slug) {
                    return `Error: Could not determine a default AI voice. Use the ${SearchVoicesTool.toolName} tool to pick a voice and pass its slug as voice_slug.`;
                }
                resolvedVoiceSlug = defaultVoice.slug;
            }

            const { voiceoverAsset: audioAsset, transcript: voiceoverTranscript } =
                await generateVoiceoverAsset({
                    text: transcript,
                    voice: resolvedVoiceSlug,
                    languageCode: language_code,
                });

            const {
                createNewVoicedrivenSceneGroup,
                setAndApplyVoiceoverAssetForSceneGroup,
                deleteSceneGroup,
            } = makeOperationsCallable(
                {
                    createNewVoicedrivenSceneGroupOperation,
                    setAndApplyVoiceoverAssetForSceneGroupOperation,
                    deleteSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_VOICEOVER,
                slot,
                {
                    deleteOtherScenes: false,
                    setPlaceholderAsset: false,
                    hasAvatar: false,
                },
                parentActionGroup,
                false,
            );

            // Apply the generated audio and load its transcript into the lexical editor
            // that the transcript tab renders from. The freshly-generated transcript is
            // passed in directly to avoid a re-fetch by asset id (not yet available for a
            // just-generated voiceover). recommendOverlays is false so this only sets up
            // the voiceover and transcript without triggering the overlay recommendation
            // flow.
            const hasAssetBeenApplied = await setAndApplyVoiceoverAssetForSceneGroup({
                sceneGroupId,
                audioAssetData: audioAsset,
                avatarAssetData: null,
                captionsLanguage: language_code,
                forceCaptionGeneration: false,
                selectedBlueprintId: null,
                generateRecommendedVideoResponse: null,
                sceneRecommendations: null,
                recommendOverlays: false,
                isGlobalVideo: true,
                transcript: voiceoverTranscript,
                systemTriggered: false,
                parentActionGroup,
            });

            if (!hasAssetBeenApplied) {
                deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                return `Error: voiceover was generated but could not be applied to the new scene group.`;
            }

            this.startBaseSceneBuilding(sceneGroupId);

            const durationSeconds = audioAsset.get('duration');
            const durationInfo =
                typeof durationSeconds === 'number'
                    ? ` Voiceover duration: ${durationSeconds.toFixed(1)}s.`
                    : '';

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroupId,
                ) ?? sceneGroupId;
            const placement =
                after_scene_group_id && after_scene_group_id.trim() !== ''
                    ? `right after scene group ${after_scene_group_id}`
                    : 'at the start of the video';
            return `Success: Added new voiceover scene group ${shortId} ${placement} with generated voiceover and transcript.${durationInfo}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding voiceover scene group: ${errorMessage}`;
        }
    }
}
